import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { useTeams } from "../hooks/useTeams";
import TruncatedText from "@/components/common/TruncatedText";

interface TeamMultiSelectProps {
  selectedTeamIds: string[];
  placeholderSelected?: string;
  onSelectionChange: (teamIds: string[]) => void;
}

export const TeamMultiSelect: React.FC<TeamMultiSelectProps> = ({
  selectedTeamIds,
  onSelectionChange,
  placeholderSelected = "selected",
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: teamsData, isLoading } = useTeams({
    isActive: true,
    sortBy: null,
    sortOrder: null,
  });
  const allTeams = useMemo(() => teamsData?.data || [], [teamsData?.data]);

  const filteredTeams = useMemo(() => {
    if (!searchTerm) return allTeams;
    return allTeams.filter((team) =>
      team.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [allTeams, searchTerm]);

  const handleSelect = (teamId: string) => {
    if (selectedTeamIds.includes(teamId)) {
      onSelectionChange(selectedTeamIds.filter((id) => id !== teamId));
    } else {
      onSelectionChange([...selectedTeamIds, teamId]);
    }
  };

  const renderPlaceholder = () => {
    if (selectedTeamIds.length < 1) {
      return "Select teams...";
    } else if (selectedTeamIds.length === 1) {
      const team = allTeams.find((t) => t.id === selectedTeamIds[0]);
      return <TruncatedText limit={25}>{team?.name || ""}</TruncatedText>;
    } else {
      return `${selectedTeamIds.length} ${placeholderSelected}`;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="new_outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          {renderPlaceholder()}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="border-b p-2">
          <div className="flex items-center rounded-md border px-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 border-0 px-0 focus-visible:ring-0"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {isLoading && (
            <div className="text-muted-foreground p-2 text-center text-sm">
              Loading...
            </div>
          )}
          {!isLoading && filteredTeams.length === 0 && (
            <div className="text-muted-foreground p-2 text-center text-sm">
              No teams found.
            </div>
          )}
          {!isLoading && filteredTeams.length !== 0 && (
            <div className="space-y-1">
              {filteredTeams.map((team) => (
                <div
                  key={team.id}
                  className={cn(
                    "hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                    selectedTeamIds.includes(team.id) && "bg-accent",
                  )}
                  onClick={() => handleSelect(team.id)}
                >
                  <div
                    className={cn(
                      "border-primary flex h-4 w-4 items-center justify-center rounded-sm border",
                      selectedTeamIds.includes(team.id)
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <Check className={cn("h-3 w-3")} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">{team.name}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedTeamIds.length > 0 && (
          <div className="bg-muted/50 border-t p-2">
            <Button
              variant="new_ghost"
              size="sm"
              className="h-8 w-full"
              onClick={() => onSelectionChange([])}
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
